import { isUserPersonProjection } from './user-projections.js';
import { isRelationshipPoint } from './relationship-projections.js';
import { isCertificationProjection } from './certification-contracts.js';
export class HouseholdResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'HouseholdResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasId = (value) => isRecord(value) && typeof value._id === 'string' && Boolean(value._id);
const isMemberSummary = (value) => hasId(value) && isUserPersonProjection(value)
  && typeof value.firstName === 'string' && typeof value.lastName === 'string'
  && (value.userId == null || typeof value.userId === 'string')
  && value.verificationCode == null
  && !['emailVerificationCode', 'emailVerificationExpires'].some(key => Object.hasOwn(value, key))
  && (value.isVerified === undefined || typeof value.isVerified === 'boolean')
  && (value.certifications == null || isCertificationProjection(value.certifications));

function validateRelationships(payload, operation) {
  const fail = (detail) => { throw new HouseholdResponseContractError(operation, detail); };
  if (payload.lineages !== undefined && (!Array.isArray(payload.lineages)
    || payload.lineages.some((lineage) => !hasId(lineage) || typeof lineage.familyName !== 'string'
      || lineage.notes != null && typeof lineage.notes !== 'string'
      || lineage.altNames !== undefined && (!Array.isArray(lineage.altNames) || lineage.altNames.some(name => typeof name !== 'string'))))) {
    fail('expected populated lineages[] with _id and familyName.');
  }
  if (payload.members !== undefined && (!Array.isArray(payload.members)
    || payload.members.some((member) => !isMemberSummary(member)))) {
    fail('expected populated members[] with _id, firstName and lastName.');
  }
  if (payload.primaryContact != null && !isMemberSummary(payload.primaryContact)) {
    fail('expected a populated primaryContact member or null.');
  }
  if (payload.venueApprover != null && (!hasId(payload.venueApprover)
    || typeof payload.venueApprover.firstName !== 'string'
    || typeof payload.venueApprover.lastName !== 'string' || !isUserPersonProjection(payload.venueApprover))) {
    fail('expected a populated venueApprover user or null.');
  }
  if (payload.community !== undefined && payload.community !== null && typeof payload.community !== 'string') {
    fail('expected a community id or null.');
  }
  for (const field of ['isActive', 'acceptVenueRequest', 'allowRepeatResidenceRequests']) {
    if (payload[field] !== undefined && typeof payload[field] !== 'boolean') {
      fail(`expected ${field} to be boolean.`);
    }
  }
  if (!isRelationshipPoint(payload.location)) {
    fail('expected GeoJSON Point coordinates [lng, lat].');
  }
  for (const field of ['displayName', 'address', 'placeId']) {
    if (payload[field] != null && typeof payload[field] !== 'string') fail(`expected ${field} text or null.`);
  }
  for (const field of ['startedAt', 'endedAt', 'createdAt', 'updatedAt']) {
    if (payload[field] != null && (typeof payload[field] !== 'string' || !Number.isFinite(Date.parse(payload[field])))) fail(`expected ${field} date or null.`);
  }
}

/** Household endpoints return direct Mongo records, never data/household envelopes. */
export function readHouseholdRecordResponse(payload, operation = 'household') {
  if (!isRecord(payload) || typeof payload._id !== 'string' || !payload._id) {
    throw new HouseholdResponseContractError(operation, 'expected a direct household with a non-empty string _id.');
  }
  validateRelationships(payload, operation);
  return payload;
}

export function readHouseholdDetailResponse(payload, operation = 'household detail') {
  readHouseholdRecordResponse(payload, operation);
  if (!Array.isArray(payload.eligibleVenueApprovers)) {
    throw new HouseholdResponseContractError(operation, 'expected eligibleVenueApprovers[].');
  }
  if (payload.eligibleVenueApprovers.some((member) => !isMemberSummary(member)
    || typeof member.bahaiId !== 'string' || typeof member.status !== 'string')) {
    throw new HouseholdResponseContractError(operation, 'expected eligibleVenueApprovers[] member summaries.');
  }
  return payload;
}

export function readHouseholdListResponse(payload) {
  if (!Array.isArray(payload)) {
    throw new HouseholdResponseContractError('household list', 'expected a direct array.');
  }
  payload.forEach((household) => readHouseholdDetailResponse(household, 'household list item'));
  return payload;
}

export function readHouseholdDeleteResponse(payload) {
  if (!isRecord(payload) || typeof payload.message !== 'string' || !payload.message) {
    throw new HouseholdResponseContractError('household deletion', 'expected { message: string }.');
  }
  return payload;
}

export function readHouseholdAddressOptionsResponse(payload) {
  if (!Array.isArray(payload) || payload.some((option) => !isRecord(option)
    || typeof option.label !== 'string' || typeof option.description !== 'string'
    || typeof option.placeId !== 'string')) {
    throw new HouseholdResponseContractError('household address options', 'expected { label, description, placeId }[].');
  }
  return payload;
}

export function readHouseholdAddressResolveResponse(payload) {
  if (!isRecord(payload) || typeof payload.formattedAddress !== 'string'
    || typeof payload.placeId !== 'string'
    || ['streetAddress', 'suburb', 'city', 'state', 'postalCode', 'countryCode', 'googleMapLink'].some(field => payload[field] != null && typeof payload[field] !== 'string')
    || (payload.lat !== null && !Number.isFinite(payload.lat))
    || (payload.lng !== null && !Number.isFinite(payload.lng))
    || payload.lat != null && Math.abs(payload.lat) > 90
    || payload.lng != null && Math.abs(payload.lng) > 180) {
    throw new HouseholdResponseContractError('household address resolution', 'expected a resolved address with formattedAddress, placeId, lat and lng.');
  }
  return payload;
}
